import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customerRequestsService } from '../../../../../services/customerRequests/customerRequestsService';
import type { CustomerAlternativeContext } from '../../../../../services/customerRequests/types';
import { useAppUserDefaultStore } from '../../../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../../../stores/appRestaurantStore';
import { useCostPriceStore } from '../../../../../stores/appCostPriceStore';
import { useOrderHistory } from '../../../../../stores/appOrderHistoryStore';
import { borderPrimary, cardBg, outlinedActionButtonClass, primaryText } from '../../../../../styling/stylingHelper';
import { resolveCostTargets } from '../../../../../utils/costTargets';
import { resolveWastePercentage } from '../../../../../utils/wasteUtils';
import CostPriceDetailModal from './CostPriceDetailModal';

/** The URL contains only a request id. Restaurant, dish and occurrence come from
 * the recipient-authorized projection, and stay reloadable until explicit close. */
export default function CustomerAlternativeRoute({ requestId, onClose }: { requestId: string; onClose: () => void }) {
    const { t } = useTranslation();
    const [context, setContext] = useState<CustomerAlternativeContext | null>(null);
    const [error, setError] = useState(false);
    const opened = useRef(false);
    const [attempt, setAttempt] = useState(0);
    const selectedRestaurantId = useAppUserDefaultStore(s => s.restaurant?.id);
    const costScope = useCostPriceStore(s => s.currentRestaurantId);
    const costLoading = useCostPriceStore(s => s.isLoading);
    const analyses = useCostPriceStore(s => s.costPriceList);
    const costError = useCostPriceStore(s => s.error);
    const orderScope = useOrderHistory(s => s.currentRestaurantId);
    const orderLoading = useOrderHistory(s => s.isLoading);
    const reportSettings = useRestaurantStore(s => s.reportSettings);

    useEffect(() => {
        let active = true;
        opened.current = false;
        setContext(null); setError(false);
        void (async () => {
            const next = await customerRequestsService.alternativeContext(requestId);
            if (!active) return;
            if (next.status === 'completed') { setContext(next); return; }
            if (!['pending', 'awaiting_customer_save'].includes(next.status)) throw new Error('Request is not awaiting customer save');
            await useRestaurantStore.getState().loadAllData();
            if (!active) return;
            const restaurant = useRestaurantStore.getState().restaurants.find(row => row.id === next.restaurant.id);
            if (!restaurant) throw new Error('Authorized restaurant is unavailable');
            useAppUserDefaultStore.getState().setRestaurant(restaurant);
            await useCostPriceStore.getState().load(restaurant.id, {
                force: true,
                isCurrent: () => active && useAppUserDefaultStore.getState().restaurant?.id === restaurant.id,
            });
            if (!active) return;
            if (useAppUserDefaultStore.getState().restaurant?.id !== restaurant.id) throw new Error('Restaurant changed while opening the question');
            const restaurants = useRestaurantStore.getState();
            const platformIds = (restaurants.restaurantSections[restaurant.id] ?? []).flatMap(section =>
                (restaurants.restaurantSectionPlatforms[section.id] ?? []).map(platform => platform.id));
            if (platformIds.length) await useOrderHistory.getState().loadByPlatformIds(restaurant.id, platformIds, restaurants.platformToSectionIndex);
            else useOrderHistory.getState().markOrderhistoryEmptyForRestaurant(restaurant.id);
            if (!active) return;
            const orders = useOrderHistory.getState();
            if (orders.currentRestaurantId !== restaurant.id || !orders.fullyLoaded) throw new Error('Order history could not be loaded');
            setContext(next);
        })().catch(failure => {
            if (!active) return;
            console.error('Failed to open customer alternative review:', failure);
            setError(true);
        });
        return () => { active = false; };
    }, [requestId, attempt]);

    const scopeMatches = context && selectedRestaurantId === context.restaurant.id && costScope === context.restaurant.id;
    const ready = scopeMatches && !costLoading && orderScope === context.restaurant.id && !orderLoading;
    const analysis = ready ? analyses.find(row => row.id === context.analysisId && row.restaurantId === context.restaurant.id) : undefined;
    const exactRows = analysis?.ingredients.filter(row => row.id === context?.ingredientId) ?? [];
    const targetMissing = ready && (!analysis || exactRows.length !== 1);
    const scopeChanged = context && context.status !== 'completed' && selectedRestaurantId !== context.restaurant.id;
    if (context && context.status !== 'completed' && !scopeChanged && (opened.current || (ready && !targetMissing && !costError && !error))) {
        opened.current = true;
        const settings = reportSettings[context.restaurant.id];
        return <CostPriceDetailModal key={requestId} costPriceId={context.analysisId} customerAlternative={context}
            costTargets={resolveCostTargets(settings?.costTargets)} defaultWastePercentage={resolveWastePercentage(settings?.wastePercentage)} onClose={onClose} />;
    }
    const failed = error || !!costError || targetMissing || scopeChanged;
    const message = context?.status === 'completed' ? 'completed' : scopeChanged ? 'contextChanged' : targetMissing ? 'targetMissing' : failed ? 'loadError' : 'loading';
    return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 dark:bg-black/60">
        <section role="dialog" aria-modal="true" aria-label={t('customerRequests.alternativeEditor.title')} className={`w-full max-w-lg space-y-4 rounded-xl border p-5 ${borderPrimary} ${cardBg} ${primaryText}`}>
            <p role={failed ? 'alert' : 'status'}>{t(`customerRequests.alternativeEditor.${message}`)}</p>
            <div className="flex flex-wrap justify-end gap-3">
                {failed && <button className={outlinedActionButtonClass} type="button" onClick={() => setAttempt(value => value + 1)}>{t('customerRequests.retry')}</button>}
                <button className={outlinedActionButtonClass} type="button" onClick={onClose}>{t('common.close')}</button>
            </div>
        </section>
    </div>;
}
